import React, {useEffect, useRef, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import './saveSearchComponent.scss';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useSearchPageType} from '../search-hooks';
import {saveSearchItem} from './duck/saveSearchComponent-actions';
import {Option} from '../../common/select/select';
import {Checkbox} from '../../common/checkbox/checkbox';
import {HibuTextButton} from '../../common/hibuTextButton/HibuTextButton';
import {HibuSearchableSelect} from '../../common/select/hibuSearchableSelect';

type SaveSearchComponentProps = {
    folderList: Option[],
    savedSearchesSharedByOthersEnabled: boolean,
}

export const SaveSearchComponent = React.memo(({
                                                   folderList,
                                                   savedSearchesSharedByOthersEnabled
                                               }: SaveSearchComponentProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const searchPageType = useSearchPageType();
    const [show, setModalShow] = useState<boolean>(false);
    const [searchName, setSearchName] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);
    const [shared, isShared] = useState<boolean>(false);
    const [folder, setFolder] = useState<Option | undefined>(undefined);

    useEffect(() => inputRef.current?.focus());

    const onClick = () => {
        setModalShow(true);
        setFolder(undefined);
    };

    const onHide = (toHide?: boolean) => {
        if (toHide) {
            setModalShow(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (folder) {
            dispatch(saveSearchItem(searchPageType, searchName, folder.value, shared));
            setModalShow(false);
        }
    };

    const onChange = (e) => {
        setSearchName(e.target.value);
    };


    const onFolderSelected = (e) => {
        setFolder(e);
    };

    const canBeSaved = (): boolean => {
        return folder && folder.value && searchName;
    };

    return (
        <div id={'save-search'}>
            <Button onClick={onClick} id={'save-search-button'}>
                {t('search.saveSearch.action.saveSearch')}
            </Button>
            <Modal
                id={'save-search-modal'}
                show={show}
                onHide={onHide}
                aria-labelledby="save-search-modal-title"
                backdropClassName={'save-search-modal-backdrop'}
            >
                <Modal.Header>
                    <Modal.Title id="save-search-modal-title">{t('search.saveSearch.action.saveSearch')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={onSubmit} validated>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>{'Name'}</Form.Label>
                            <Form.Control required type="text" onChange={onChange} ref={inputRef}/>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('search.saveSearch.action.folderTitle')}</Form.Label>
                            <HibuSearchableSelect
                                className={'form-control'}
                                isSearchable={true}
                                onChange={onFolderSelected}
                                value={folder}
                                options={folderList}
                                placeholder={t('search.saveSearch.action.folderPlaceholder')}
                            />
                        </Form.Group>
                        {savedSearchesSharedByOthersEnabled && (
                            <Form.Group>
                                <Form.Label>{t('search.saveSearch.action.sharedTitle')}</Form.Label>
                                <Checkbox
                                    className={'form-control-checkbox'}
                                    labelKey={t('search.saveSearch.action.share')}
                                    onChange={isShared}
                                    value={shared}
                                    mayEdit={true}
                                />
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <HibuTextButton
                            onClick={onHide}
                            label={t('general.cancel')}
                            disabled={false}
                            type={'button'}
                        />
                        <HibuTextButton
                            onClick={() => onHide(canBeSaved())}
                            type={'submit'}
                            label={t('general.save')}
                            disabled={!canBeSaved()}
                            filled
                        />
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
});

